<template>
  <div class="cash-register-entries">
    <div class="loading mt-2" v-if="loading"></div>
    <template v-else>
      <div class="empty" v-if="items.length === 0">
        <div class="empty-icon">
          <fa-icon :icon="['fal', 'info-circle']" size="2x"/>
        </div>
        <p class="empty-subtitle">{{ emptyMessage }}</p>
      </div>
      <div class="cr-table-scroll" v-else>
      <table class="table table-hover">
        <thead>
        <tr>
          <th style="width: 150px">Hora</th>
          <th>Tipo</th>
          <th>Paciente ou destino</th>
          <th class="hide-sm">Formas</th>
          <th class="text-right">Valor</th>
          <th style="width: 150px"></th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="item in items" :key="item.id" :class="{'text-gray': item.status === 'reversed'}">
          <td class="text-nowrap">{{ item.date | date('datetime') }}</td>
          <td>
            <span class="label cr-type" :class="`cr-type-${item.type}`">
              {{ typeName(item.type) }}
            </span>
            <small class="cr-entry-note text-error" v-if="item.status === 'reversed'">
              estornado
            </small>
            <small class="cr-entry-note text-gray"
                   v-else-if="item.type === 'reversal' && item.reversal">
              {{ item.reversal.reason }}
            </small>
          </td>
          <td>
            <template v-if="item.patient">{{ item.patient.name }}</template>
            <template v-else-if="item.entity">{{ item.entity.name }}</template>
            <template v-else-if="item.bankAccount">{{ item.bankAccount.name }}</template>
            <small class="text-gray"
                   v-if="item.payer && item.patient && item.payer.name !== item.patient.name">
              · pago por {{ item.payer.name }}
            </small>
          </td>
          <td class="hide-sm">{{ methodsOf(item) }}</td>
          <td class="text-right text-bold cr-entry-value"
              :class="{'text-error': isNegative(item), 'text-success': !isNegative(item)}">
            {{ isNegative(item) ? '' : '+ ' }}{{ signedValue(item) | currency }}
          </td>
          <td class="text-right text-nowrap cr-entry-actions">
            <button class="btn btn-sm btn-action btn-icon btn-gray tooltip-left"
                    :class="{loading: printingId === item.id, tooltip: printingId !== item.id}"
                    data-tooltip="Recibo" @click="print(item)" :disabled="!!printingId">
              <fa-icon :icon="['fal', 'print']"/>
            </button>
            <button class="btn btn-sm btn-action btn-icon tooltip-left"
                    :class="[
                      hasInvoice(item) ? 'btn-secondary' : 'btn-gray',
                      {
                        loading: invoiceLoadingFor(item),
                        tooltip: !invoiceLoadingFor(item),
                      },
                    ]"
                    :data-tooltip="hasInvoice(item) ? 'Ver nota fiscal' : 'Emitir NFS-e'"
                    :disabled="invoice.loading"
                    v-if="canInvoice(item)" @click="openInvoice(item)">
              <fa-icon :icon="['fal', 'file-invoice-dollar']"/>
            </button>
            <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                    data-tooltip="Estornar" v-if="canReverse(item)" @click="$emit('reverse', item)">
              <fa-icon :icon="['fal', 'undo']"/>
            </button>
            <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                    data-tooltip="Cancele a nota fiscal antes de estornar"
                    v-else-if="allowReverse && hasBlockingInvoice(item)" disabled>
              <fa-icon :icon="['fal', 'undo']"/>
            </button>
          </td>
        </tr>
        </tbody>
      </table>
      </div>
    </template>

    <invoice-detail-or-create
      v-if="invoice.show"
      :show="invoice.show"
      :id="invoice.id"
      :total-amount="invoice.entry.totals.paid"
      :entity="invoiceEntity(invoice.entry)"
      origin-type="cash_register_entry"
      :origin-id="invoice.entry.id"
      @loading="invoice.loading = $event"
      @changed="invoice.changed = true"
      @close="closeInvoice"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import {
  REVERSE_FINANCIAL_TRANSACTION,
  CREATE_FISCAL_DOCUMENT,
} from '@/data/actions/modules/financial';
import { getName as typeName } from '@/data/cash-register-entry-types';
import InvoiceDetailOrCreate from '@/components/fiscal-document/FiscalDocument.vue';

const FISCAL_BLOCKING_STATUSES = [
  'pending',
  'emitting',
  'issued',
  'cancelling',
  'cancellation_rejected',
  'cancellation_failed',
];

export default {
  components: {
    InvoiceDetailOrCreate,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    allowReverse: {
      type: Boolean,
      default: true,
    },
    emptyMessage: {
      type: String,
      default: 'Nenhum movimento neste caixa.',
    },
  },
  data() {
    return {
      printingId: null,
      invoice: {
        show: false,
        loading: false,
        id: null,
        entry: null,
        changed: false,
      },
    };
  },
  computed: {
    ...mapState({
      branch: ({ auth }) => auth.user.branch,
    }),
    fiscalEnabled() {
      const settings = this.branch.settings.fiscalDocument;
      return !!(settings && settings.enabled);
    },
  },
  methods: {
    typeName,
    invoiceLoadingFor(item) {
      return this.invoice.loading && !!this.invoice.entry && this.invoice.entry.id === item.id;
    },
    hasInvoice(item) {
      return !!(item.fiscalDocument && item.fiscalDocument.id);
    },
    canInvoice(item) {
      return this.fiscalEnabled
        && item.type === 'receipt'
        && item.status === 'completed'
        && !item.totals.courtesy
        && (this.hasInvoice(item) || this.$can(CREATE_FISCAL_DOCUMENT));
    },
    invoiceEntity(entry) {
      const { payer, patient } = entry;
      const id = payer && payer.entityId ? payer.entityId : patient.id;
      return { id, name: payer && payer.name ? payer.name : patient.name };
    },
    openInvoice(item) {
      this.invoice = {
        show: true,
        loading: false,
        id: this.hasInvoice(item) ? item.fiscalDocument.id : null,
        entry: item,
        changed: false,
      };
    },
    closeInvoice() {
      this.invoice.show = false;
      this.invoice.loading = false;
      if (this.invoice.changed) {
        this.$emit('refresh');
      }
    },
    methodsOf(item) {
      if (!item.payments || item.payments.length === 0) return '';
      return item.payments.map((payment) => {
        const holder = payment.holder && item.patient && payment.holder.name !== item.patient.name
          ? ` (saldo de ${payment.holder.name})`
          : '';
        const installments = payment.installments > 1 ? ` ${payment.installments}x` : '';
        return `${payment.method.name}${installments}${holder}`;
      }).join(', ');
    },
    isNegative(item) {
      return ['reversal', 'withdrawal', 'expense'].includes(item.type)
        && !(item.type === 'reversal' && item.reversal && item.reversal.ofType === 'expense');
    },
    signedValue(item) {
      const value = item.type === 'receipt' ? item.totals.paid : item.value;
      return this.isNegative(item) ? -value : value;
    },
    hasBlockingInvoice(item) {
      return this.hasInvoice(item) && FISCAL_BLOCKING_STATUSES.includes(item.fiscalDocument.status);
    },
    canReverse(item) {
      return this.allowReverse
        && item.status === 'completed'
        && ['receipt', 'expense'].includes(item.type)
        && !this.hasBlockingInvoice(item)
        && this.$can(REVERSE_FINANCIAL_TRANSACTION);
    },
    print(item) {
      this.printingId = item.id;
      this.$file.print(`/cash-register/entries/${item.id}/receipt`)
        .catch(() => {})
        .finally(() => {
          this.printingId = null;
        });
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/_variables.scss";
.cash-register-entries {
  .text-nowrap {
    white-space: nowrap;
  }
  .cr-type {
    border-radius: $border-radius;
    font-size: $font-size-xs;
    font-weight: 600;
    &.cr-type-receipt {
      background-color: lighten($success-color, 45%);
      color: darken($success-color, 12%);
    }
    &.cr-type-reversal {
      background-color: lighten($error-color, 38%);
      color: darken($error-color, 8%);
    }
    &.cr-type-supply {
      background-color: lighten($info-color, 40%);
      color: darken($info-color, 12%);
    }
    &.cr-type-withdrawal, &.cr-type-expense {
      background-color: lighten($warning-color, 38%);
      color: darken($warning-color, 22%);
    }
  }
  .cr-entry-note {
    display: block;
    font-size: $font-size-xs;
    margin-top: .1rem;
  }
  .cr-entry-value {
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .cr-entry-actions {
    .btn + .btn {
      margin-left: .2rem;
    }
  }
  tr.text-gray {
    .cr-type {
      opacity: .6;
    }
    .cr-entry-value {
      color: $gray-color;
      text-decoration: line-through;
    }
  }
}
</style>
